import { inject, Injectable } from '@angular/core';

import { AlertController, AlertOptions, ModalController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { includes, isEmpty, isEqual, isNull } from 'lodash-es';
import { combineLatest, firstValueFrom, map } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

import {
  PrimarySubscriptionAccessActionEnum,
  PrimarySubscriptionPlanAccessAliasEnum,
  StripePurchaseEnum
} from '../../enums/monetization';
import * as fromMonetizationActions from '../../store/actions/monetization.actions';
import * as fromMonetizationSelectors from '../../store/selectors/monetization.selectors';
import * as fromWalkingFarmSelectors from '../../store/selectors/walking-farm.selectors';
import { PaywallModalComponent } from '../components/paywall-modal/paywall-modal.component';

interface AccessFeatureOptions {
  accessAction: PrimarySubscriptionAccessActionEnum;
  onboardingPaywall: boolean;
  preventPaywall: boolean;
}

const defaultAccessFeatureOptions: AccessFeatureOptions = {
  accessAction: PrimarySubscriptionAccessActionEnum.ViewFarm,
  onboardingPaywall: false,
  preventPaywall: false
};

@Injectable({
  providedIn: 'root'
})
export class MonetizationService {
  private store = inject(Store);
  private modalController = inject(ModalController);
  private alertController = inject(AlertController);

  totalSavedFarms = 0;
  freeAllowedFarms = 2;

  constructor() {
    this.store
      .pipe(
        select(fromMonetizationSelectors.selectPrimarySubscriptionPlatformProductIdentifier),
        filter((platformProductIdentifier) => !isNull(platformProductIdentifier))
      )
      .subscribe(() => {
        this.store.dispatch(fromMonetizationActions.loadPrimarySubscriptionDetails());
      });

    this.store
      .pipe(
        select(fromMonetizationSelectors.selectStorePlatformProductIdentifiers),
        filter((storePlatformProductIdentifier) => !isEmpty(storePlatformProductIdentifier)),
        distinctUntilChanged((previous, current) => isEqual(previous, current))
      )
      .subscribe(() => {
        this.store.dispatch(fromMonetizationActions.loadStoreWebDetails());
      });

    this.store.pipe(select(fromWalkingFarmSelectors.selectTotalSavedFarms)).subscribe((totalSavedFarms) => {
      this.totalSavedFarms = totalSavedFarms;
    });
  }

  private hasAccessToAction(
    isPlanActive: boolean,
    planAccessAlias: PrimarySubscriptionPlanAccessAliasEnum | null,
    options: Partial<AccessFeatureOptions>
  ) {
    let hasAccess = isPlanActive;
    if (!hasAccess && planAccessAlias === PrimarySubscriptionPlanAccessAliasEnum.C) {
      switch (options.accessAction) {
        case PrimarySubscriptionAccessActionEnum.CreateFarm:
          hasAccess = this.totalSavedFarms < this.freeAllowedFarms;
          break;
        case PrimarySubscriptionAccessActionEnum.ViewFarm:
          hasAccess = this.totalSavedFarms <= this.freeAllowedFarms;
          break;
        case PrimarySubscriptionAccessActionEnum.ViewSavedFarmsList:
          hasAccess = true;
          break;
        default:
          break;
      }
    }
    return hasAccess;
  }

  accessFeature(planAccessAliases: PrimarySubscriptionPlanAccessAliasEnum[], options?: Partial<AccessFeatureOptions>) {
    const composedOptions = { ...defaultAccessFeatureOptions, ...options };
    return firstValueFrom(
      combineLatest([
        this.store.pipe(select(fromMonetizationSelectors.selectPrimarySubscriptionAccessAlias)),
        this.store.pipe(select(fromMonetizationSelectors.selectPrimarySubscriptionIsActive)),
        this.store.pipe(select(fromMonetizationSelectors.selectPrimarySubscriptionIsEnabled))
      ]).pipe(
        map(async ([accessAlias, isActive, isEnabled]) => {
          const hasAccess = isEnabled
            ? includes(planAccessAliases, accessAlias)
              ? this.hasAccessToAction(isActive, accessAlias, composedOptions)
              : true
            : true;

          if (accessAlias == PrimarySubscriptionPlanAccessAliasEnum.C) {
            await this.handlePlanCAlertsAndPaywall({ composedOptions, hasAccess, isActive });
          } else if (!hasAccess && !composedOptions.preventPaywall) {
            await this.showPrimarySubscriptionPlanPaywall(composedOptions);
          }
          return hasAccess;
        })
      )
    );
  }

  async handlePlanCAlertsAndPaywall({
    composedOptions,
    hasAccess,
    isActive
  }: {
    composedOptions: AccessFeatureOptions;
    hasAccess: boolean;
    isActive: boolean;
  }) {
    const alertOptions: AlertOptions = {
      backdropDismiss: false,
      buttons: [
        {
          role: 'cancel',
          text: 'Continue'
        },
        {
          role: 'confirm',
          text: 'Subscribe'
        }
      ],
      header: '',
      message: ''
    };

    if (!isActive && composedOptions.accessAction === PrimarySubscriptionAccessActionEnum.ViewSavedFarmsList) {
      alertOptions.header = 'Free Tier Limits';
      alertOptions.message =
        `Without a subscription, you may only retain ${this.freeAllowedFarms} saved farm reports.` +
        ` You may continue to access all your farm reports with a valid subscription.`;
      const alertConfirm = await this.alertController.create(alertOptions);
      await alertConfirm.present();
      alertConfirm.onDidDismiss().then(async ({ role }) => {
        if (role === 'confirm') {
          await this.showPrimarySubscriptionPlanPaywall(composedOptions);
        }
      });
    }
    if (!hasAccess && !composedOptions.preventPaywall) {
      alertOptions.header = 'Free Tier Limit Reached';
      alertOptions.message =
        `Without a subscription, you are only allowed to maintain ${this.freeAllowedFarms}` +
        ` saved farm reports and you currently have ${this.totalSavedFarms}.` +
        ` You must either delete excess farm reports or purchase a subscription.`;

      const alertConfirm = await this.alertController.create(alertOptions);
      await alertConfirm.present();
      const { role } = await alertConfirm.onDidDismiss();
      if (role === 'confirm') {
        await this.showPrimarySubscriptionPlanPaywall(composedOptions);
      }
    }
  }

  async showPrimarySubscriptionPlanPaywall(options?: Partial<AccessFeatureOptions>) {
    const composedOptions = { ...defaultAccessFeatureOptions, ...options };
    const modal = await this.modalController.create({
      component: PaywallModalComponent,
      componentProps: {
        onboardingPaywall: composedOptions.onboardingPaywall,
        paywallInformation$: this.store.pipe(
          select(fromMonetizationSelectors.selectPrimarySubscriptionPaywallInformation)
        ),
        type: StripePurchaseEnum.Subscription
      }
    });

    await modal.present();
    return modal;
  }

  async showError(options: { error: Error; header?: string; subHeader?: string }) {
    const composedOptions = {
      ...{
        header: 'Purchase Module Error'
      },
      ...options
    };
    const alert = await this.alertController.create({
      backdropDismiss: false,
      buttons: [
        {
          role: 'cancel',
          text: 'Ok'
        }
      ],
      header: composedOptions.header,
      message: composedOptions.error.message ?? 'An unknown error has ocurred.',
      subHeader: composedOptions.subHeader
    });
    await alert.present();
  }
}
